import { useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrFestlegungen } from './typen';
import { zeitpunkt } from './kopf';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, Feld, StatusBadge, formatiereZahl,
  type Spalte,
} from '../../muster';

type Festlegung = MsrFestlegungen['festlegungen'][number];
type Klemmenlauf = MsrFestlegungen['klemmen']['protokoll'][number];
type Effizienz = MsrFestlegungen['effizienz'][number];

/**
 * Die Grundlagen: was der Fachplaner am 16.09.2026 festgelegt hat
 * (Fragen 64 bis 79).
 *
 * --- Warum das auf die Seite gehört ----------------------------------------
 *
 * Wer einen Kanaldruck-Sollwert von 247 Pa liest, muss sehen können, WARUM
 * 50 Pa aufgeschlagen werden, seit wann und auf wessen Wort. Eine Grundlage,
 * die nur in einer Migration steht, findet niemand — und dann wird die Zahl
 * angezweifelt statt die Festlegung nachgelesen.
 *
 * --- Warum das Offene dazugehört -------------------------------------------
 *
 * Eine Liste, die nur die beantworteten Fragen zeigt, sieht vollständig aus.
 * Die Zahl der Elektrozähler, die Segmentlast des M-Bus und der Umfang
 * Beleuchtung/Sonnenschutz sind es nicht — und genau das sind die Zeilen, die
 * ein Prüfer sehen muss.
 */
export function Festlegungen({
  api, projektId, daten, onNeuLaden,
}: {
  api: ApiClient;
  projektId: string;
  daten: MsrFestlegungen;
  onNeuLaden: () => void;
}) {
  // TM-8c: Erfolg als Meldung (aria-live), Fehler als ErrorState - vorher standen beide im selben `div.hinweis`.
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [fehler, setFehler] = useState<{ titel: string; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [frage, setFrage] = useState<'freigeben' | 'aufheben' | null>(null);
  const [bemerkung, setBemerkung] = useState('');

  const alle = daten.festlegungen ?? [];
  const offen = alle.filter((f) => !f.istBestaetigt);

  const wert = (f: MsrFestlegungen['festlegungen'][number]) => {
    const zahl = f.zahlwert == null
      ? null
      : formatiereZahl(Math.round(f.zahlwert * 100) / 100);

    return [zahl, f.einheit, f.textwert].filter(Boolean).join(' ');
  };

  const freigeben = async () => {
    setLaeuft(true);
    setHinweis(null);
    setFehler(null);
    try {
      const f = await api.msrKlemmenFreigeben(projektId, bemerkung);
      setHinweis(`${f.klemmen} Klemmen in ${f.asps} Schränken eingefroren. `
        + 'Ab jetzt vergibt kein Lauf eine vergebene Klemme neu.');
      onNeuLaden();
    } catch (e) {
      setFehler({ titel: 'Die Klemmenzuteilung wurde nicht freigegeben.', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
      setFrage(null);
      setBemerkung('');
    }
  };

  const aufheben = async () => {
    setLaeuft(true);
    setHinweis(null);
    setFehler(null);
    try {
      await api.msrKlemmenFreigabeAufheben(projektId, bemerkung);
      setHinweis('Freigabe aufgehoben — die Zuteilung darf sich wieder verschieben.');
      onNeuLaden();
    } catch (e) {
      setFehler({ titel: 'Die Freigabe wurde nicht aufgehoben.', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
      setFrage(null);
      setBemerkung('');
    }
  };

  const effizienzProjekt = (daten.effizienz ?? []).find((e) => e.ebene === 'projekt');
  const effizienzAnlagen = (daten.effizienz ?? []).filter((e) => e.ebene === 'anlage');
  const effizienzGewerke = (daten.effizienz ?? []).filter((e) => e.ebene === 'gewerk');

  const festlegungSpalten: Spalte<Festlegung>[] = [
    { schluessel: 'frage', titel: 'Frage', art: 'zahl', breite: '80px',
      wert: (f) => f.frage },
    {
      schluessel: 'schluessel', titel: 'Gegenstand', immerSichtbar: true,
      wert: (f) => f.schluessel,
      inhalt: (f) => (
        <span title={f.wortlaut ?? undefined}>
          {f.schluessel}
          {f.zielfeld && (
            <div className="schwach" style={{ fontSize: 11 }}>wirkt an {f.zielfeld}</div>
          )}
        </span>
      ),
    },
    { schluessel: 'wert', titel: 'Wert', breite: '170px', wert: (f) => wert(f),
      inhalt: (f) => <strong>{wert(f)}</strong> },
    {
      schluessel: 'stand', titel: 'Stand', art: 'status', breite: '130px',
      wert: (f) => (f.istBestaetigt ? 'bestätigt' : 'offen'),
      inhalt: (f) => (f.istBestaetigt
        ? <StatusBadge klein zustand="gut" text="bestätigt" />
        : <StatusBadge klein zustand="warnung" text="offen" />),
    },
    { schluessel: 'quelle', titel: 'Quelle', wert: (f) => f.quelle },
  ];

  const effizienzSpalten: Spalte<Effizienz>[] = [
    { schluessel: 'kennung', titel: 'Gewerk', wert: (e) => e.kennung, immerSichtbar: true },
    {
      schluessel: 'erreichteKlasse', titel: 'Klasse', art: 'status', breite: '140px',
      wert: (e) => e.erreichteKlasse,
      inhalt: (e) => (
        <StatusBadge
          klein
          zustand={e.erreichteKlasse === e.zielKlasse ? 'gut' : 'warnung'}
          text={e.erreichteKlasse}
          titel={`Ziel ${e.zielKlasse}`}
        />
      ),
    },
    { schluessel: 'erfuellt', titel: 'Anlagen mit Zielklasse', sortierbar: false,
      wert: (e) => `${e.erfuellt} von ${e.gefordert}` },
  ];

  const protokollSpalten: Spalte<Klemmenlauf>[] = [
    { schluessel: 'freigegebenAm', titel: 'Wann', art: 'datum', breite: '180px',
      wert: (f) => f.freigegebenAm,
      inhalt: (f) => zeitpunkt(f.freigegebenAm) },
    { schluessel: 'freigegebenVon', titel: 'Wer', wert: (f) => f.freigegebenVon },
    {
      schluessel: 'istFreigabe', titel: 'Was', art: 'status', breite: '130px',
      wert: (f) => (f.istFreigabe ? 'Freigabe' : 'Aufhebung'),
      inhalt: (f) => (f.istFreigabe
        ? <StatusBadge klein zustand="gut" text="Freigabe" />
        : <StatusBadge klein zustand="warnung" text="Aufhebung" />),
    },
    {
      schluessel: 'umfang', titel: 'Umfang / Bemerkung', immerSichtbar: true,
      wert: (f) => `${f.klemmen} Klemmen, ${f.asps} Schränke`
        + (f.bemerkung ? ` — ${f.bemerkung}` : ''),
    },
  ];

  return (
    <>
      {hinweis && <p className="schwach" role="status" aria-live="polite">{hinweis}</p>}
      {fehler && <ErrorState klein titel={fehler.titel} technisch={fehler.text} />}

      <ContentSection
        titel="Festlegungen des Fachplaners vom 16.09.2026"
        beschreibung={'Die Antworten auf die sechzehn offenen Fragen der Normprüfung '
          + '(Fragen 64 bis 79). Jede Zeile trägt ihre Quelle und den Wortlaut der '
          + 'Antwort — eine Entscheidung lässt sich nicht nachrechnen, sie gilt, weil '
          + 'jemand sie getroffen hat.'}
        meta={offen.length > 0
          ? <StatusBadge zustand="warnung" text={`${offen.length} offen`} />
          : <StatusBadge zustand="gut" text="alle bestätigt" />}
      >

        <DataTable
          name="Festlegungen"
          spalten={festlegungSpalten}
          zeilen={alle}
          schluessel={(f) => f.id}
          dicht
          ausfuhr
          ausfuhrDateiname="msr-festlegungen"
          leer={(
            <EmptyState
              titel="Für dieses Projekt ist keine Festlegung hinterlegt."
              erklaerung={'Die Festlegungen kommen aus der Normprüfung — ohne sie gelten '
                + 'die Vorlagenwerte der Spezifikation.'}
            />
          )}
        />

        {offen.length > 0 && (
          <p style={{ fontSize: 13, marginBottom: 0 }}>
            <strong>{offen.length} Festlegung(en) offen:</strong>{' '}
            {offen.map((f) => f.begruendung).join(' ')}
          </p>
        )}
      </ContentSection>

      {effizienzProjekt && (
        <ContentSection titel="GA-Effizienzklasse nach DIN EN ISO 52120-1 (Frage 65)">

          <p style={{ fontSize: 13, marginTop: 0 }}>
            Ziel <strong>{effizienzProjekt.zielKlasse}</strong>, erreicht{' '}
            <StatusBadge
              klein
              zustand={effizienzProjekt.erreichteKlasse === effizienzProjekt.zielKlasse ? 'gut' : 'warnung'}
              text={effizienzProjekt.erreichteKlasse}
              titel={`Ziel ${effizienzProjekt.zielKlasse}`}
            />{' '}
            — {effizienzProjekt.erfuellt} von {effizienzProjekt.gefordert} bewerteten
            Anlagen tragen die Zielklasse.
          </p>

          <p className="schwach" style={{ fontSize: 12 }}>
            {effizienzProjekt.begruendung}
          </p>

          {effizienzGewerke.length > 0 && (
            <DataTable
              name="Effizienzklasse je Gewerk"
              spalten={effizienzSpalten}
              zeilen={effizienzGewerke}
              schluessel={(e) => e.id}
              dicht
              suche={false}
              spaltenwahl={false}
              leer={<EmptyState klein titel="Keine Gewerkebewertung vorhanden." />}
            />
          )}

          <p className="schwach" style={{ fontSize: 12, marginBottom: 0 }}>
            {effizienzAnlagen.filter((e) => e.erreichteKlasse !== e.zielKlasse).length}{' '}
            von {effizienzAnlagen.length} Anlagen verfehlen die Zielklasse; jede davon
            steht als Befund <code>effizienzklasse_verfehlt</code> mit den fehlenden
            Funktionen.
          </p>
        </ContentSection>
      )}

      <ContentSection titel="Klemmenzuteilung freigeben (Frage 76)">

        <p style={{ fontSize: 13, marginTop: 0 }}>
          Stand:{' '}
          <strong>
            {daten.klemmen.eingefroren ? 'eingefroren' : 'noch beweglich'}
          </strong>{' '}
          — {daten.klemmen.freigegeben} freigegebene und {daten.klemmen.beweglich}{' '}
          bewegliche Klemme(n).
        </p>

        <p className="schwach" style={{ fontSize: 12 }}>
          Solange die Zuteilung beweglich ist, kann jeder Lauf einen Datenpunkt auf
          einen anderen Kanal legen. Nach der Freigabe bleibt jede vergebene Klemme
          stehen; wo die Rechnung heute einen anderen Kanal ergäbe, entsteht ein
          Befund <code>klemme_eingefroren_konflikt</code> statt einer stillen
          Verschiebung.
        </p>

        <button type="button" className="m-knopf" disabled={laeuft}
          onClick={() => {
            setBemerkung('');
            setFrage(daten.klemmen.eingefroren ? 'aufheben' : 'freigeben');
          }}>
          {daten.klemmen.eingefroren ? 'Freigabe aufheben' : 'Freigeben und einfrieren'}
        </button>

        <DataTable
          name="Protokoll der Freigaben"
          spalten={protokollSpalten}
          zeilen={daten.klemmen.protokoll}
          schluessel={(f) => f.id}
          dicht
          suche={false}
          spaltenwahl={false}
          leer={(
            <EmptyState
              klein
              titel="Die Klemmenzuteilung wurde noch nie freigegeben."
              erklaerung={'Solange sie beweglich ist, kann jeder Lauf einen Datenpunkt '
                + 'auf einen anderen Kanal legen.'}
            />
          )}
        />
      </ContentSection>

      {/* Bisher zwei `window.prompt` — dort ließ sich der Text weder prüfen
          noch abbrechen-mit-Fokus, und ESC ergab bei „Aufheben" ein stilles
          Nichts. Jetzt ein Dialog mit Pflichtfeld (Auftrag Abschnitt 16). */}
      <ConfirmDialog
        offen={frage === 'freigeben'}
        art="warnung"
        titel="Klemmenzuteilung freigeben und einfrieren?"
        text={'Ab der Freigabe bleibt jede vergebene Klemme stehen; wo die Rechnung '
          + 'heute einen anderen Kanal ergäbe, entsteht ein Befund statt einer stillen '
          + 'Verschiebung.'}
        bestaetigenText="Freigeben und einfrieren"
        laeuft={laeuft}
        beiBestaetigen={() => void freigeben()}
        beiAbbrechen={() => setFrage(null)}
      >
        <Feld beschriftung="Gegen welchen Planstand wird freigegeben?" pflicht
          wert={bemerkung} beiAenderung={(w) => setBemerkung(w as string)} />
      </ConfirmDialog>

      <ConfirmDialog
        offen={frage === 'aufheben'}
        art="warnung"
        titel="Freigabe der Klemmenzuteilung aufheben?"
        text={'Danach dürfen sich Klemmen wieder verschieben — wer nach der alten Liste '
          + 'verdrahtet, verdrahtet falsch.'}
        bestaetigenText="Freigabe aufheben"
        laeuft={laeuft}
        beiBestaetigen={() => void aufheben()}
        beiAbbrechen={() => setFrage(null)}
      >
        <Feld beschriftung="Begründung" pflicht
          fehler={bemerkung.trim() === '' ? 'Eine Begründung ist Pflicht.' : undefined}
          wert={bemerkung} beiAenderung={(w) => setBemerkung(w as string)} />
      </ConfirmDialog>
    </>
  );
}
